import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'

import { OrganisationBanner } from './organisation-banner'
import { Sidebar } from './sidebar'
import { Topbar } from './topbar'

const COLLAPSED_STORAGE_KEY = 'fleetcore.sidebar.collapsed'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Two sidebars, not one responsive sidebar: on desktop it is a persistent rail
 * that collapses to icons, on tablet and phone it is an overlay drawer. Trying
 * to make one element do both ends up with a drawer that keeps a 56px stub of
 * itself on screen and steals a third of a phone's width.
 */
export function AppShell() {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  const toggleCollapsed = () => {
    setCollapsed((previous) => {
      const next = !previous
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next))
      } catch {
        /* Storage denied: the preference lasts for this tab only. */
      }
      return next
    })
  }

  return (
    <div className="fc-safe-shell flex h-full w-full overflow-hidden bg-background">
      <a href="#fc-main" className="fc-skip-link">
        {t('common:skipToContent')}
      </a>

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        className="hidden lg:flex"
      />

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            role="presentation"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground/40"
          />
          <div className="fc-safe-drawer absolute inset-y-0 start-0 flex animate-fade-in">
            <Sidebar collapsed={false} onNavigate={() => setDrawerOpen(false)} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label={t('common:sidebar.closeMenu')}
              className="m-2 flex h-9 w-9 items-center justify-center rounded-md bg-surface text-foreground-muted shadow-popover"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenMenu={() => setDrawerOpen(true)} />
        <OrganisationBanner />
        <main id="fc-main" className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
